import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionButton from "./ActionButton";
import { useTranslation } from "react-i18next";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { sendReminder } from "@pulse/api/surveyEmailAPI";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "frontend-components";
import { faBell, faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { useCurrentSurvey } from "@pulse/hooks/surveyHooks";
import { queryClient } from "@pulse/queryClient";
import { getSurveysOptions, getSurveyOptions } from "@pulse/api/queryApi";
import usePulseId from "@pulse/hooks/usePulseId";

type ReminderDialogPhase = "confirm" | "success";

function SendReminderModal({
    sendReminderEmailsMutation,
}: {
    sendReminderEmailsMutation: UseMutationResult<{ failed: number }, Error, { surveyId: number }>;
}) {
    const { i18n, t } = useTranslation();
    const { data: survey } = useCurrentSurvey();
    return (
        <AlertDialogContent size="sm">
            <AlertDialogHeader>
                <FontAwesomeIcon icon={faCircleInfo} className="text-info" size="4x" />

                <AlertDialogTitle>{t("responseOverview.sendReminder")}</AlertDialogTitle>

                <AlertDialogDescription>
                    {t("responseOverview.sendReminderInfo")}
                </AlertDialogDescription>
            </AlertDialogHeader>
            {survey?.reminderSentAt && (
                <p className="text-center text-body-sm text-text-muted">
                    {t("responseOverview.lastSentReminder", {
                        lastSent: new Date(survey.reminderSentAt).toLocaleDateString(i18n.language),
                    })}
                </p>
            )}
            <AlertDialogFooter>
                <AlertDialogCancel
                    variant="outline"
                    disabled={sendReminderEmailsMutation.isPending}
                >
                    {t("common.cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                    variant="fill"
                    disabled={sendReminderEmailsMutation.isPending}
                    onClick={() => sendReminderEmailsMutation.mutate({ surveyId: survey!.id })}
                >
                    {sendReminderEmailsMutation.isPending ? t("common.sending") : t("common.send")}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    );
}

function SendReminderSuccessModal({ failedCount }: { failedCount: number }) {
    const { t } = useTranslation();

    return (
        <AlertDialogContent size="sm">
            <AlertDialogHeader>
                <FontAwesomeIcon icon={faCircleInfo} className="text-info" size="4x" />

                <AlertDialogTitle>{t("survey.reminderSent")}</AlertDialogTitle>

                {failedCount > 0 && (
                    <AlertDialogDescription className="text-error">
                        {t("survey.failedEmails", { count: failedCount })}
                    </AlertDialogDescription>
                )}
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel variant="fill">{t("common.ok")}</AlertDialogCancel>
            </AlertDialogFooter>
        </AlertDialogContent>
    );
}

export default function SendReminder() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [dialogPhase, setDialogPhase] = useState<ReminderDialogPhase>("confirm");
    const [failedCount, setFailedCount] = useState<number | null>(null);
    const { data: survey } = useCurrentSurvey();
    const pulseId = usePulseId();
    const sendReminderEmailsMutation = useMutation({
        mutationFn: sendReminder,
        onSuccess: (response) => {
            setFailedCount(response.failed);
            setDialogPhase("success");
            queryClient.invalidateQueries(getSurveyOptions({ surveyId: survey!.id }));
            queryClient.invalidateQueries(getSurveysOptions({ pulseInstanceId: pulseId }));
        },
    });

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);

        if (nextOpen) {
            sendReminderEmailsMutation.reset();
            setFailedCount(null);
            setDialogPhase("confirm");
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={handleOpenChange}>
            <AlertDialogTrigger
                render={
                    <ActionButton label={t("responseOverview.labelSendReminder")}>
                        <FontAwesomeIcon icon={faBell} size="xl" />
                    </ActionButton>
                }
            />
            {dialogPhase === "success" ? (
                <SendReminderSuccessModal failedCount={failedCount!} />
            ) : (
                <SendReminderModal sendReminderEmailsMutation={sendReminderEmailsMutation} />
            )}
        </AlertDialog>
    );
}
